import { VinFieldProps, SuggestionTextField } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { useActions } from "@tm/morpheus"
import { initSelector } from "@tm/utils"
import { useSelector } from "react-redux"
import GeneralVehicleForm from "@bundles/damage-calculation-dat/components/_shared/GeneralVehicleFields"
import { styled, Box } from "@tm/components"
import { useCallback, useMemo } from "react"
import { filterModelsByInitialRegistration } from "../../../data/helpers"
import { useContainer } from "../../../data/repositories"
import { MainActions, vehicleFieldsSelector, vehicleModelsSelector, vehicleSelector } from "../../main/business"
import { getEurotaxVehicleSchema } from "../business/helpers/getEurotaxSchemas"

const selector = initSelector(vehicleSelector, vehicleFieldsSelector, vehicleModelsSelector)

export default function VehicleFields() {
    const { translateText } = useLocalization()
    const actions = useActions(MainActions, "updateVehicleInfoFields", "updateFieldValue", "setValidateFields")

    const [vehicle, fields, vehicleModels] = useSelector(selector)
    const { models } = vehicleModels
    const eurotaxSchema = useMemo(() => getEurotaxVehicleSchema(translateText), [translateText])

    let filteredVehicle = models
    const showEquipmentFilter = filteredVehicle?.some((x) => x.equipment)

    if (fields.initialRegistration && models) {
        filteredVehicle = filterModelsByInitialRegistration(fields.initialRegistration, models)
    }

    const handleGetEquipmentPackageSuggestions = useCallback((): Array<string> => {
        return (
            models
                ?.map((model) => model.equipment)
                .distinct()
                .filter((model) => !!model) ?? []
        )
    }, [models])

    const handleEquipmentPackageOnConfirm = useCallback(
        (equipmentPackage: string) => {
            actions.updateVehicleInfoFields({ ...fields, selectedEquipmentPackage: equipmentPackage })
        },
        [actions, fields]
    )

    const handleGetVinSuggestions = useCallback(
        (query: string): Promise<Array<string>> => {
            return useContainer().action("getVinSuggestions")(query, vehicle!.tecDocTypeId)
        },
        [vehicle]
    )

    const onHandleUpdateValue = useCallback(
        (path: string, value: string | Date | number) => {
            actions?.updateFieldValue(path, value)
        },
        [actions]
    )

    const handleFormValid = useCallback(
        (isValid: boolean) => {
            actions.setValidateFields("vehicleValid", isValid)
        },
        [actions]
    )

    const vinFieldProps: VinFieldProps = useMemo(
        () => ({
            getSuggestionDataAsync: handleGetVinSuggestions,
        }),
        [handleGetVinSuggestions]
    )

    return (
        <GeneralVehicleForm
            fields={fields}
            onUpdateFieldValue={onHandleUpdateValue}
            vehicle={vehicle}
            onFormValid={handleFormValid}
            schema={eurotaxSchema}
            vinFieldProps={vinFieldProps}
            disableRegistrationNo
        >
            {showEquipmentFilter && (
                <Col>
                    <SuggestionTextField
                        floatingLabel
                        label={translateText(1484)}
                        value={fields?.selectedEquipmentPackage}
                        getSuggestionData={handleGetEquipmentPackageSuggestions}
                        onChangeConfirm={handleEquipmentPackageOnConfirm}
                    />
                </Col>
            )}
        </GeneralVehicleForm>
    )
}

const Col = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.5, 1, 0.5, 0),
    width: "12em",
    ".suggest__box": {
        padding: "4.25em 0.1em 0.5em",
    },

    ".suggest__box .vin-search .btn": {
        textWrap: "wrap",
    },
    ".suggest ": {
        height: "100%",
    },
}))
